/*
 * utils.js 05.12.2017
 *
 * (c) 2017 ergovia GmbH
 */

import isBefore from 'date-fns/is_before';
import addDays from 'date-fns/add_days';
import isWeekend from 'date-fns/is_weekend';
import isAfter from 'date-fns/is_after';
import isSameDay from 'date-fns/is_same_day';
import subDays from 'date-fns/sub_days';
import eachDay from 'date-fns/each_day';
import areRangesOverlapping from 'date-fns/are_ranges_overlapping';

/**
 * Prüft nach, ob das übergebene Ziel kurz davor ist, die Deadline zu erreichen. Kurz davor bedeuet 14 Tage vor Deadline.
 *
 * @param {Object} goal das Ziel, das überprüft werden soll
 * @return {Boolean} true, wenn das Ziel kurz vor der Erreichung der Deadline ist, andernfalls false
 */
export const checkNearDeadline = goal => isBefore(goal.ende, addDays(new Date(), 14));

/**
 * Zählt wie viele der übergebenen Ziele kurz davor sind ihre Deadline zu erreichen.
 *
 * @param {Array} goals eine Liste von Zielen
 * @return {Number} die Anzahl der Ziele auf die die Filterbedingung zutrifft
 */
export const countNearDeadline = goals => goals.filter(g => !g.zielerreicht).filter(checkNearDeadline).length;

/**
 * Zählt wie viele der übergebenen Ziele ihre Deadline überschritten haben.
 *
 * @param {Array} goals eine Liste von Zielen
 * @return {Number} die Anzahl der Ziele auf die die Filterbedingung zutrifft
 */
export const countDeadlineExceeded = goals => goals.filter(g => !g.zielerreicht).filter(g => isBefore(g.ende, new Date())).length;

/**
 * Zählt wie viele der übergebenen Ziele keine Dokumentation zu den Schritten zur Zielerreichung aufweisen.
 *
 * @param {Array} goals eine Liste von Zielen
 * @return {Number} die Anzahl der Ziele auf die die Filterbedingung zutrifft
 */
export const countMissingSteps = goals => goals.filter(g => !g.schrittVorhanden).length;

/**
 * Prüft, ob wir mindestens ein nicht abgeschlossenes, relevantes Ziel für den heutigen Tag hinterlegt haben.
 *
 * @param {Array} goals eine Liste von Zielen
 * @return {boolean} true, wenn es für den heutigen Tag ein nicht offenes Ziel gibt, ansonsten false
 */
export const hasRelevantOpenGoalsForToday = goals => goals.filter(g => !g.zielerreicht)
    .filter(g => isAfter(new Date(), g.anfang) || isSameDay(new Date(), g.anfang))
    .filter(g => isBefore(new Date(), g.ende) || isSameDay(new Date(), g.ende)).length > 0;

/**
 * Comparator für die Sortierung von Teilnehmern nach Vornamen.
 *
 * @param {Object} a der linke Teilnehmer
 * @param {Object} b der rechte Teilnehmer
 * @return {Number} die jeweilige Einsortierung von Teilnehmer A
 */
const sortByFirstname = (a, b) => a.vorname.localeCompare(b.vorname);

/**
 * Comparator für die Sortierung von Teilnehmern nach Nachnamen.
 *
 * @param {Object} a der linke Teilnehmer
 * @param {Object} b der rechte Teilnehmer
 * @return {Number} die jeweilige Einsortierung von Teilnehmer A
 */
export const sortByLastname = (a, b) => {
    const compared = a.nachname.localeCompare(b.nachname);
    return compared === 0 ? sortByFirstname(a, b) : compared;
};


/**
 * Comparator für die Sortierung von Teilnehmern nach ihren Deadlines. Ein Teilnehmer A steht weiter oben wenn mehr Ziele als bei Teilnehmer B nahe ihrer Deadlines sind.
 * Sollte der Wert bei beiden Teilnehmern gleich sein, wird eine Namensortierung angewandt. Sind gar keine Ziele hinterlegt, wird diese Teilnehmer automatisch nach hinten sortiert.
 *
 * @param {Object} a der linke Teilnehmer
 * @param {Object} b der rechte Teilnehmer
 * @return {Number} die jeweilige Einsortierung von Teilnehmer A
 */
export const sortByNearDeadline = (a, b) => {

    const aNearDeadline = a.ziele.length === 0 ? -1 : countNearDeadline(a.ziele),
        bNearDeadline = b.ziele.length === 0 ? -1 : countNearDeadline(b.ziele);


    if (aNearDeadline > bNearDeadline) {
        return -1;
    }

    if (aNearDeadline < bNearDeadline) {
        return 1;
    }

    return sortByLastname(a, b);

};

/**
 * Comparator für die Sortierung von Teilnehmern nach ihren Deadlines. Ein Teilnehmer A steht weiter oben wenn mehr Ziele als bei Teilnehmer B ihre Deadline überschritten haben.
 * Sollte der Wert bei beiden Teilnehmern gleich sein, wird die Anzahl der Ziele genommen deren Deadlines in kurzer Zeit ablaufen.
 *
 * @param {Object} a der linke Teilnehmer
 * @param {Object} b der rechte Teilnehmer
 * @return {Number} die jeweilige Einsortierung von Teilnehmer A
 */
export const sortByDeadline = (a, b) => {

    const aDeadlineExceeded = countDeadlineExceeded(a.ziele),
        bDeadlineExceeded = countDeadlineExceeded(b.ziele);

    if (aDeadlineExceeded > bDeadlineExceeded) {
        return -1;
    }

    if (aDeadlineExceeded < bDeadlineExceeded) {
        return 1;
    }

    return sortByNearDeadline(a, b);
};

/**
 * Comparator für die Sortierung von Teilnehmern nach ihren Schritten zur Zielerreichung. Ein Teilnehmer A steht weiter oben, wenn mehr Ziele als bei Teilnehmer B eine fehlende
 * Dokumentation von Schritten zur Zielerreichung aufweisen. Sollte der Wert bei beiden Teilnehmern gleich sein, wird eine Namensortierung angewandt.
 *
 * @param {Object} a der linke Teilnehmer
 * @param {Object} b der rechte Teilnehmer
 * @return {Number} die jeweilige Einsortierung von Teilnehmer A
 */
export const sortByStep = (a, b) => {
    const aMissingStep = countMissingSteps(a.ziele),
        bMissingStep = countMissingSteps(b.ziele);

    if (aMissingStep > bMissingStep) {
        return -1;
    }

    if (aMissingStep < bMissingStep) {
        return 1;
    }

    return sortByLastname(a, b);
};

/**
 * Comparator für die aufsteigende Sortierung von Zielen nach ihrem Ende-Datum bzw. dem Datum der Zielerreichung, falls das Ziel bereits erreicht wurde.
 *
 * @param {Object} a das linke Ziel
 * @param {Object} b das rechte Ziel
 * @return {Number} die jeweilige Einsortierung von Ziel A
 */
export const sortGoalsByEnde = (a, b) => {
    const endeA = a.zielerreicht ? a.zielerreichtAm < a.ende ? a.zielerreichtAm : a.ende : a.ende,
        endeB = b.zielerreicht ? b.zielerreichtAm < b.ende ? b.zielerreichtAm : b.ende : b.ende;

    return isBefore(endeA, endeB) ? -1 : 1;
};

/**
 * Prüft ob es Zeiträume gibt in denen keine Zieldokumentation zu finden ist. Der Prüfzeitraum beginnt mit dem Anfangsdatum des ersten Ziels und hört mit dem Enddatum des letzten
 * Ziels auf. Sollte eine Lücke genau auf ein Wochenende fallen, wird diese herausgefiltert, da dies fachlich nicht als Lücke gilt.
 *
 * @param {Array} ziele die Ziele für die Prüfung
 * @return {boolean} true wenn es Lücken in der Zieldokumentation gibt, false wenn nicht.
 */
export const hasGaps = ziele => {

    const gaps = [];

    // Sicherstellen, dass die Ziele stets nach Ende-Datum bzw. "zielerreichtAm" aufsteigend sortiert sind.
    ziele.sort(sortGoalsByEnde);

    for (let i = 1; i < ziele.length; i++) {

        const beginningOfHole = ziele[i - 1].zielerreicht ? ziele[i - 1].zielerreichtAm : ziele[i - 1].ende,
            endOfHole = ziele[i].anfang;

        if (isAfter(endOfHole, beginningOfHole)) {

            const gapStart = addDays(beginningOfHole, 1),
                gapEnd = subDays(endOfHole, 1);

            if (isBefore(gapStart, gapEnd) && eachDay(gapStart, gapEnd).filter(day => !isWeekend(day)).length > 0) {
                gaps.push({anfang: gapStart, ende: gapEnd});
            }
        }
    }

    return gaps.length > 0;
};

/**
 * Comparator für die Sortierung von Teilnehmer nach Zielen. Ein Teilnehmer steht weiter oben wenn keine Ziele vorhanden sind. Haben sowohl TN A als auch TN B Ziele, wird eine
 * Sortierung nach Nachnamen herangezogen
 * @param {Object} a der linke Teilnehmer
 * @param {Object} b der rechte Teilnehmer
 * @return {Number} die jeweilige Einsortierung von Teilnehmer A
 */
export const sortByGoals = (a, b) => {

    if (a.ziele.length === 0 && b.ziele.length !== 0) {
        return -1;
    }

    if (a.ziele.length !== 0 && b.ziele.length === 0) {
        return 1;
    }

    const nichtErreichteZieleA = a.ziele.filter(z => !z.zielerreicht).length,
        nichtErreichteZieleB = b.ziele.filter(z => !z.zielerreicht).length;

    if (nichtErreichteZieleA === 0 && nichtErreichteZieleB !== 0) {
        return -1;
    }

    if (nichtErreichteZieleA !== 0 && nichtErreichteZieleB === 0) {
        return 1;
    }

    const hasAGaps = hasGaps(a.ziele),
        hasBGaps = hasGaps(b.ziele);

    if (hasAGaps && !hasBGaps) {
        return -1;
    }
    if (!hasAGaps && hasBGaps) {
        return 1;
    }

    return sortByLastname(a, b);
};

/**
 * Prüft ob sich das linke Objekt gegenüber dem rechten Objekt geändert hat. Bei der ersten Ungleichheit wird die Schleife abgebrochen und das Objekt gilt als geändert.
 *
 * @param {Object} left das Objekt welches mit dem rechten verglichen werden soll
 * @param {Object} right das andere Objekt
 * @return {boolean} true wenn es sich geändert hat, false wenn nicht
 */
export const hasChanged = (left, right) => {

    let changed = false;

    for (const key of Object.keys(right)) {

        if (left[key] !== right[key]) {
            changed = true;
            break;
        }

    }

    return changed;

};


/**
 * Prüft, ob es ein Ziel gibt, dass das aktuelle umschließt (früherer Start, späteres Ende)
 *
 * @param {Object} current Zu betrachtendes Ziel
 * @param {Array} targets Alle Ziele
 * @return {boolean} Ist das Ziel von einem größeren Zeitraum umgeben?
 */
function isInsideLongerTarget(current, targets) {
    const currentBegin = current.anfang,
        currentEnd = current.zielerreicht ? current.zielerreichtAm : current.ende;
    for (let i = 0; i < targets.length; i++) {
        const e = targets[i],
            compareBegin = e.anfang,
            compareEnd = e.zielerreicht ? e.zielerreichtAm : e.ende;
        if (current.id !== e.id && currentBegin >= compareBegin && currentEnd <= compareEnd) {
            return true;
        }
    }
    return false;
}


/**
 * Prüft ob es Zeiträume gibt in denen keine Zieldokumentation zu finden ist und liefert diese Lücken zurück.
 * Der Prüfzeitraum beginnt mit dem Anfangsdatum des ersten Ziels und hört mit dem Enddatum des letzten  Ziels auf. Sollte eine Lücke genau auf ein Wochenende fallen,
 * wird diese herausgefiltert, da dies fachlich nicht als Lücke gilt.
 *
 * @param {Array} ziele die Ziele für die Prüfung
 * @return {Array} ein Array mit den Zeiträumen in denen keine Zieldoku zu finden ist
 */
// eslint-disable-next-line complexity
export const getGaps = ziele => {

    const gaps = [],
        goalsWithoutOverlaps = [];


    for (let i = 1; i < ziele.length; i++) {

        const current = ziele[i - 1],
            next = ziele[i];

        const initialBegin = current.anfang,
            initialEnd = current.zielerreicht ? current.zielerreichtAm : current.ende,
            compareBegin = next.anfang,
            compareEnd = next.zielerreicht ? next.zielerreichtAm : next.ende;

        const overlapped = areRangesOverlapping(
            initialBegin, initialEnd,
            compareBegin, compareEnd
        );

        if (overlapped) {

            goalsWithoutOverlaps.push({
                anfang: isBefore(initialBegin, compareBegin) ? initialBegin : compareBegin,
                ende: isAfter(initialEnd, compareEnd) ? initialEnd : compareEnd
            });

        } else {
            if (!isInsideLongerTarget(current, ziele)) {
                goalsWithoutOverlaps.push(current);
                if (ziele.length - 1 === i) {
                    goalsWithoutOverlaps.push(next);
                }
            }
        }
    }

    for (let i = 1; i < goalsWithoutOverlaps.length; i++) {

        goalsWithoutOverlaps.sort(sortGoalsByEnde);

        const beginningOfHole = goalsWithoutOverlaps[i - 1].zielerreicht ? goalsWithoutOverlaps[i - 1].zielerreichtAm : goalsWithoutOverlaps[i - 1].ende,
            endOfHole = goalsWithoutOverlaps[i].anfang;

        if (isAfter(endOfHole, beginningOfHole)) {
            const gapStart = addDays(beginningOfHole, 1),
                gapEnd = subDays(endOfHole, 1);

            if (isBefore(gapStart, gapEnd) && eachDay(gapStart, gapEnd).filter(day => !isWeekend(day)).length > 0) {
                gaps.push({anfang: gapStart, ende: gapEnd});
            }

        }
    }


    return gaps;
};





// WEBPACK FOOTER //
// ./src/de/ergovia/utils.js